import { useSelector } from "react-redux";
import { createSelector } from "reselect";

const selectAppSlice = state => state.app;
const selectMultimediaSlice = state => state.multimedia;
const selectResourceSlice = state => state.resource;
const selectFormSlice = state => state.form;
const EMPTY_ARRAY = [];

// app slice
export const selectFormList = createSelector(
  selectAppSlice,
  app => app.formList
);

export const selectOpenModal = createSelector(selectAppSlice, app => app.modal);
export const selectNotification = createSelector(selectAppSlice, app => app.notification);

export const selectShowingActionSuccessMessage = createSelector(
  selectAppSlice,
  app => app.showingActionSuccessMessage
);
export const selectTheSuccessfulAction = createSelector(
  selectAppSlice,
  app => app.theSuccessfulAction
);
export const selectDraftList = createSelector(
  selectAppSlice,
  app => app.draftList
);
export const selectDispatchList = createSelector(
  selectAppSlice,
  app => app.dispatchList
);
export const selectSelectedForm = createSelector(
  selectAppSlice,
  app => app.selectedForm
);

// multimedia slice
export const selectCurrentSelectedMultimediaInput = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.currentSelectedMultimediaInput
);
export const selectIsOpen = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.isOpen
);
export const selectImagesToSubmit = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.imagesToSubmit
);
export const selectImageDraftValues = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.imageDraftValues
);

export const selectBarcodeValues = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.barcodeValues
);

export const selectImageInputValues = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.imageInputValues
);

export const selectLocationValues = createSelector(
  selectMultimediaSlice,
  multimedia => multimedia.locationValues
);

// resource slice
export const selectResources = createSelector(
  selectResourceSlice,
  resource => resource.resources
);

export const selectResourceLoadCounter = createSelector(
  selectResourceSlice,
  resource => resource.resourceLoadCounter
);

export const selectResourceMap = createSelector(
  selectResourceSlice,
  resource => resource.resourcesMap
);
export const selectLoadedResources = createSelector(
  selectResourceSlice,
  resource => resource.loadedResources
);

export const selectResourceLoading = createSelector(selectResourceSlice, resource => resource.resourcesLoading);

// form slice
export const selectIsLoading = createSelector(selectFormSlice, form => form.isLoading);

export const selectIsSubmitting = createSelector(selectFormSlice, form => form.isSubmitting);

export const selectResourceVersions = createSelector(
  selectFormSlice,
  form => form.resourceVersions
);
export const selectRules = createSelector(
  selectFormSlice,
  form => form.rules
);
export const selectFieldErrors = createSelector(
  selectFormSlice,
  form => form.fieldErrors
);
export const selectFieldErrorById = (state, identifier) =>
  selectFieldErrors(state)[identifier];
export const selectMetadataInputs = createSelector(
  selectFormSlice,
  form => form.metadataInputs
);
export const selectRunningProcesses = createSelector(
  selectFormSlice,
  form => form.runningProcesses
);
export const selectRequiredResourcesLoading = createSelector(
  selectFormSlice,
  form => form.requiredResourcesLoading
);
export const selectGroupList = createSelector(
  selectFormSlice,
  form => form.groupList
);
export const selectGroupListByIdentifier = (state, identifier) =>
  selectGroupList(state)[identifier] || EMPTY_ARRAY;
export const selectHasGroupListIdentifier = (state, identifier) =>
  Object.prototype.hasOwnProperty.call(selectGroupList(state), identifier);
export const selectRepeatGroupFields = createSelector(
  selectFormSlice,
  form => form.repeatGroupFields
);
export const selectDispatchAnswers = createSelector(
  selectFormSlice,
  form => form.dispatchAnswers
);
export const selectRootLevelFields = createSelector(
  selectFormSlice,
  form => form.rootLevelFields
);
export const selectRepeatGroupList = createSelector(
  selectFormSlice,
  form => form.repeatGroupList
);
export const selectFieldsWithExpressions = createSelector(
  selectFormSlice,
  form => form.fieldsWithExpressions
);

export const selectFieldResourceUpdateHighlights = createSelector(
  selectFormSlice,
  form => form.fieldResourceUpdateHighlights
);
export const selectSubformFields = createSelector(
  selectFormSlice,
  form => form.subformFields
);
export const selectInitialAnswers = createSelector(
  selectFormSlice,
  form => form.initialAnswers
);
export const selectSelectResourceOptions = createSelector(
  selectFormSlice,
  form => form.selectResourceOptions
);
export const selectDraftAnswers = createSelector(
  selectFormSlice,
  form => form.draftAnswers
);
export const selectFieldsToWatch = createSelector(
  selectFormSlice,
  form => form.fieldsToWatch
);
export const selectSelectFieldResources = createSelector(
  selectFormSlice,
  form => form.selectFieldResources
);
export const selectSubforms = createSelector(
  selectFormSlice,
  form => form.subforms
);
export const selectFieldsWithInitAnsExpr = createSelector(
  selectFormSlice,
  form => form.fieldsWithInitAnsExpr
);

export const selectAreProcessesRunning = createSelector(
  selectFormSlice,
  form => form.runningProcesses.length > 0
);



const useGlobalSelector = () => {
    // app slice
    const formList = useSelector(selectFormList);
    const showingActionSuccessMessage = useSelector(selectShowingActionSuccessMessage);
    const theSuccessfulAction = useSelector(selectTheSuccessfulAction);
    const draftList = useSelector(selectDraftList);
    const dispatchList = useSelector(selectDispatchList);
    const isLoading = useSelector(selectIsLoading);
    const selectedForm = useSelector(selectSelectedForm);
    const selectedModal = useSelector(selectOpenModal);
    const selectedNotification = useSelector(selectNotification);

    // multimedia slice
    const currentSelectedMultimediaInput = useSelector(selectCurrentSelectedMultimediaInput);
    const isOpen = useSelector(selectIsOpen);
    const imagesToSubmit = useSelector(selectImagesToSubmit);
    const imageDraftValues = useSelector(selectImageDraftValues);
    const barcodeValues = useSelector(selectBarcodeValues);
    const imageInputValues = useSelector(selectImageInputValues);
    const locationValues = useSelector(selectLocationValues);

    // resource slice
    const resources = useSelector(selectResources);
    const resourceLoadCounter = useSelector(selectResourceLoadCounter);
    const resourcesMap = useSelector(selectResourceMap);
    const loadedResources = useSelector(selectLoadedResources);
    const resourcesLoading = useSelector(selectResourceLoading);
    
    // form slice
    const resourceVersions = useSelector(selectResourceVersions);
    const rules = useSelector(selectRules);
    const fieldErrors = useSelector(selectFieldErrors);
    const metadataInputs = useSelector(selectMetadataInputs);
    const runningProcesses = useSelector(selectRunningProcesses);
    const areProcessesRunning = useSelector(selectAreProcessesRunning);
    const requiredResourcesLoading = useSelector(selectRequiredResourcesLoading);
    const draftAnswers = useSelector(selectDraftAnswers);
    const repeatGroupFields = useSelector(selectRepeatGroupFields);
    const dispatchAnswers = useSelector(selectDispatchAnswers);
    const rootLevelFields = useSelector(selectRootLevelFields);
    const repeatGroupList = useSelector(selectRepeatGroupList);
    const groupList = useSelector(selectGroupList);
    const fieldResourceUpdateHighlights = useSelector(selectFieldResourceUpdateHighlights);
    const subformFields = useSelector(selectSubformFields);
    const initialAnswers = useSelector(selectInitialAnswers);
    const selectResourceOptions = useSelector(selectSelectResourceOptions);
    const fieldsWithExpressions = useSelector(selectFieldsWithExpressions);
    const fieldsToWatch = useSelector(selectFieldsToWatch);
    const selectFieldResources = useSelector(selectSelectFieldResources);
    const subforms = useSelector(selectSubforms);
    const fieldsWithInitAnsExpr = useSelector(selectFieldsWithInitAnsExpr);
    const isSubmitting = useSelector(selectIsSubmitting);

    return {
        formList,
        isOpen,
        currentSelectedMultimediaInput,
        showingActionSuccessMessage,
        theSuccessfulAction,
        selectedForm,
        resources,
        resourceVersions,
        rules,
        imagesToSubmit,
        imageDraftValues,
        fieldErrors,
        metadataInputs,
        runningProcesses,
        requiredResourcesLoading,
        draftAnswers,
        repeatGroupFields,
        dispatchAnswers,
        rootLevelFields,
        repeatGroupList,
        groupList,
        fieldResourceUpdateHighlights,
        subformFields,
        barcodeValues,
        imageInputValues,
        initialAnswers,
        resourceLoadCounter,
        draftList,
        dispatchList,
        locationValues,
        resourcesMap,
        loadedResources,
        selectResourceOptions,
        fieldsWithExpressions,
        fieldsToWatch,
        selectFieldResources,
        subforms,
        fieldsWithInitAnsExpr,
        isLoading,
        areProcessesRunning,
        selectedModal,
        selectedNotification,
        isSubmitting,
        resourcesLoading
    };
};

export default useGlobalSelector;
